import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  Button,
  ButtonProps,
  Icon,
  IconButton,
  Link,
  Tooltip,
} from "@chakra-ui/react";
import { useConfigStore } from "hooks/useConfigStore";
import React from "react";
import { INetwork, IRpcEndpoint } from "types/internal";
import { Explorer } from "types/state";
import { short } from "utils/web3js";

export type AddressType = "tx" | "account";

const explorerOpts: Record<
  Exclude<Explorer, "none">,
  {
    label: string;
    supportedNetworks: INetwork[];
    url: (
      valueType: AddressType,
      value: string,
      rpcEndpoint: IRpcEndpoint
    ) => string;
  }
> = {
  solana: {
    label: "Open in Solana Explorer",
    supportedNetworks: ["devnet", "testnet", "mainnet-beta", "custom"],
    url: (valueType, value, rpcEndpoint) =>
      `https://explorer.solana.com/${
        valueType === "account" ? "address" : valueType
      }/${value}?cluster=${
        rpcEndpoint.custom ? "custom" : rpcEndpoint.network
      }${
        rpcEndpoint.custom
          ? "&customUrl=" + encodeURIComponent(rpcEndpoint.url)
          : ""
      }`,
  },
  solanafm: {
    label: "Open in SolanaFM",
    supportedNetworks: ["devnet", "testnet", "mainnet-beta", "custom"],
    url: (valueType, value, rpcEndpoint) =>
      `https://solana.fm/${
        valueType === "account" ? "address" : valueType
      }/${value}?cluster=${
        rpcEndpoint.custom
          ? encodeURIComponent(rpcEndpoint.url)
          : rpcEndpoint.network === "devnet"
          ? "devnet-qn1"
          : rpcEndpoint.network === "testnet"
          ? "testnet-qn1"
          : "mainnet-qn1"
      }`,
  },
  solanaBeach: {
    label: "Open in Solana Beach",
    supportedNetworks: ["devnet", "testnet", "mainnet-beta"],
    url: (valueType, value, rpcEndpoint) =>
      `https://solanabeach.io/${
        valueType === "account" ? "address" : "transaction"
      }/${value}${
        rpcEndpoint.network === "devnet" || rpcEndpoint.network === "testnet"
          ? "?cluster=" + rpcEndpoint.network
          : ""
      }`,
  },
  solscan: {
    label: "Open in Solscan",
    supportedNetworks: ["devnet", "testnet", "mainnet-beta", "custom"],
    url: (valueType, value, rpcEndpoint) =>
      `https://solscan.io/${valueType}/${value}?cluster=${
        rpcEndpoint.custom ? "custom" : rpcEndpoint.network
      }${
        rpcEndpoint.custom
          ? "&customUrl=" + encodeURIComponent(rpcEndpoint.url)
          : ""
      }`,
  },
};

export const ExplorerButton: React.FC<
  {
    value: string;
    valueType: AddressType;
    rpcEndpoint: IRpcEndpoint;
    variant: "button" | "long-link" | "short-link";
  } & Omit<ButtonProps, "aria-label">
> = ({
  value,
  valueType,
  rpcEndpoint,
  size,
  isDisabled,
  variant,
  children,
  ...theRest
}) => {
  const explorer = useConfigStore((state) => state.appOptions.explorer);

  if (explorer === "none") return null; // hide
  const opts = explorerOpts[explorer];
  const disabled =
    isDisabled || !opts.supportedNetworks.includes(rpcEndpoint.network);
  const href = !disabled ? opts.url(valueType, value, rpcEndpoint) : undefined;

  return (
    <Tooltip label={opts.label} isDisabled={disabled}>
      {variant === "long-link" || variant === "short-link" ? (
        children || (
          <Button as={Link} variant="link" href={href} isExternal {...theRest}>
            {variant === "short-link" ? short(value) : value}{" "}
            <ExternalLinkIcon ml="1" />
          </Button>
        )
      ) : (
        <IconButton
          as={Link}
          variant="ghost"
          href={href}
          isExternal
          icon={
            explorer === "solscan" ? (
              <SolscanIcon size={size} />
            ) : explorer === "solanafm" ? (
              <SolanaFmIcon size={size} />
            ) : explorer === "solanaBeach" ? (
              <SolanaBeachIcon size={size} />
            ) : (
              <SolanaExplorerIcon size={size} />
            )
          }
          aria-label={opts.label}
          size={size}
          isDisabled={disabled}
          {...theRest}
        />
      )}
    </Tooltip>
  );
};

const SolscanIcon: React.FC<{ size: any }> = ({ size }) => (
  <Icon
    w={size === "xs" ? 3 : size === "sm" ? 4 : 5}
    h={size === "xs" ? 3 : size === "sm" ? 4 : 5}
    viewBox="0 0 16 16"
  >
    <path
      d="m 8.021296,4.8390002 c 1.7612,0.016 3.1512,1.43932 3.1263,3.1999 -0.025,1.76122 -1.4605,3.1505998 -3.2147,3.1115998 -1.7343,-0.0384 -3.0943,-1.4310198 -3.0898,-3.1634398 0.0045,-1.75994 1.4214,-3.16406 3.1782,-3.14806 z"
      fill="#c74ae3"
      id="path44"
    />
    <path
      d="m 12.117696,14.7319 c -2.7513,2.2156 -7.6676,1.4227 -10.2141,-1.559 -2.80119999,-3.2798898 -2.47409999,-8.2006898 0.7386,-11.1145198 3.1736,-2.87798497 8.0835,-2.71927097 11.0767,0.35839 2.9055,2.98742 3.0342,7.9440298 0.3149,10.7414298 -0.6349,-0.665 -1.2704,-1.3312 -1.9366,-2.0288 1.031,-1.4411798 1.3504,-3.0904098 0.7341,-4.8612298 -0.9203,-2.64376 -3.8354,-4.03891 -6.4939,-3.13782 -2.6271,0.89021 -4.0651,3.71828 -3.2447,6.38059 0.8294,2.6917598 3.6428,4.2206598 6.3429,3.3912598 0.5374996,-0.1651 0.8358,-0.0685 1.1833,0.3315 0.4595,0.5299 0.9913,0.9971 1.4988,1.4982 z"
      fill="#00e8b5"
      id="path46"
    />
  </Icon>
);

const SolanaExplorerIcon: React.FC<{ size: any }> = ({ size }) => (
  <Icon
    w={size === "xs" ? 3 : size === "sm" ? 4 : 5}
    h={size === "xs" ? 3 : size === "sm" ? 4 : 5}
    mt="1"
    ml="1"
    viewBox="0 0 16 16"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="m 2.1360235,8.473284 c 0.03974,-0.04273 0.08776,-0.07707 0.141181,-0.100939 0.05345,-0.02387 0.111152,-0.03678 0.169731,-0.03797 h 9.7687335 c 0.04133,5.82e-4 0.0816,0.01299 0.115993,0.03577 0.0344,0.02278 0.06144,0.05493 0.07784,0.09258 0.01643,0.03768 0.02154,0.07924 0.01474,0.119724 -0.0068,0.04048 -0.02527,0.07813 -0.05313,0.108453 l -2.070655,2.282692 c -0.03974,0.04273 -0.08776,0.07707 -0.141182,0.100939 -0.05342,0.02386 -0.111151,0.03678 -0.1697035,0.03797 H 0.21462048 c -0.04133,-5.82e-4 -0.08162,-0.01299 -0.115994,-0.03577 -0.0344,-0.02278 -0.06144,-0.05493 -0.07787,-0.09258 -0.0164,-0.03768 -0.0215099996,-0.07924 -0.01471,-0.119724 0.0068,-0.04048 0.02527,-0.07813 0.05313,-0.108453 z M 12.380425,6.581063 c 0.02789,0.03032 0.04633,0.068 0.05313,0.108479 0.0068,0.04048 0.0017,0.08205 -0.01471,0.119698 -0.01643,0.03768 -0.04347,0.06982 -0.07784,0.09258 -0.0344,0.02278 -0.07469,0.03519 -0.11602,0.03577 L 2.4531555,6.94529 C 2.3945755,6.94409 2.3368715,6.93119 2.2834255,6.90732 2.2300055,6.88345 2.1819845,6.84911 2.1422435,6.806382 L 0.05605748,4.531415 c -0.02786,-0.03032 -0.0463,-0.068 -0.05313,-0.108479 -0.0068,-0.04048 -0.0017,-0.08205 0.01474,-0.119698 0.01643,-0.03768 0.04344,-0.06982 0.07784,-0.09258 0.0344,-0.02278 0.07467,-0.03519 0.115993,-0.03577 l 9.77185702,-0.0077 c 0.058549,0.0012 0.1162835,0.0141 0.1697025,0.03797 0.05342,0.02386 0.101442,0.05821 0.141182,0.100938 z M 2.1360235,0.138909 c 0.03974,-0.04273 0.08776,-0.07707 0.141181,-0.100939 C 2.3306545,0.0141 2.3883565,0.00119 2.4469355,0 l 9.7749505,0.0077 c 0.04133,5.83e-4 0.0816,0.01299 0.115994,0.03577 0.0344,0.02275 0.06141,0.0549 0.07784,0.09258 0.01643,0.03765 0.02154,0.07922 0.01474,0.119697 -0.0068,0.04048 -0.02527,0.07816 -0.05313,0.108479 l -2.076873,2.274967 c -0.03974,0.04273 -0.08776,0.07707 -0.141182,0.100939 -0.05342,0.02387 -0.111151,0.03678 -0.1697035,0.03797 H 0.21462048 c -0.04133,-5.83e-4 -0.08162,-0.01299 -0.115994,-0.03577 -0.0344,-0.02278 -0.06144,-0.05493 -0.07787,-0.09258 -0.0164,-0.03768 -0.0215099996,-0.07924 -0.01471,-0.119724 0.0068,-0.04048 0.02527,-0.07813 0.05313,-0.108452 z"
      fill="#14f195"
      id="path14"
      // style="stroke-width:0.264583"
    />
  </Icon>
);

const SolanaFmIcon: React.FC<{ size: any }> = ({ size }) => (
  <Icon
    w={size === "xs" ? 3 : size === "sm" ? 4 : 5}
    h={size === "xs" ? 3 : size === "sm" ? 4 : 5}
    viewBox="0 0 26.8748 38.9958"
  >
    <path
      d="M 0,19.3864 C 0.0359734,14.2379 2.10426,9.31209 5.75459,5.68122 9.40493,2.05036 14.3418,0.00844106 19.4904,0 h 1.4271 V 3.09897 H 19.512 C 15.1814,3.10111 11.0269,4.81381 7.95291,7.86425 4.87895,10.9147 3.13438,15.0559 3.09897,19.3864 Z"
      fill="currentColor"
      id="path2"
    />
    <path
      d="m 5.94434,38.9958 v -3.099 H 7.37146 C 11.7013,35.8918 15.854,34.177 18.9259,31.1254 21.9977,28.0739 23.74,23.9327 23.7737,19.6029 h 3.1011 c -0.036,5.1504 -2.1058,10.0778 -5.7584,13.709 -3.6527,3.6312 -8.5923,5.6718 -13.74277,5.6774 z"
      fill="currentColor"
      id="path4"
    />
    <path
      d="M 5.94434,33.0512 V 29.9523 H 7.37146 C 10.1253,29.949 12.767,28.861 14.7244,26.9239 c 1.9574,-1.9371 3.0728,-4.5673 3.1047,-7.321 h 3.099 c -0.0342,3.5743 -1.4771,6.9907 -4.0154,9.5075 -2.5382,2.5167 -5.9668,3.9305 -9.54124,3.9344 z"
      fill="currentColor"
      id="path6"
    />
    <path
      d="M 5.94434,19.3864 C 5.97744,15.8094 7.42125,12.3899 9.96188,9.8716 12.5025,7.35328 15.9345,5.93967 19.5118,5.93811 h 1.4271 v 3.09897 h -1.4271 c -2.7557,3.8e-4 -5.4002,1.08712 -7.3599,3.02452 -1.9597,1.9374 -3.07668,4.5693 -3.1086,7.3248 z"
      fill="currentColor"
      id="path8"
    />
    <path
      d="m 5.94434,27.1068 v -3.0989 h 1.42712 c 1.17788,-8e-4 2.30883,-0.4618 3.15154,-1.2848 0.8428,-0.8229 1.3306,-1.9425 1.3594,-3.1201 v -0.1147 c 0.004,-2.0201 0.8099,-3.9558 2.2405,-5.382 1.4306,-1.4261 3.3689,-2.2259 5.3889,-2.2236 h 1.4271 v 3.099 h -1.4271 c -1.1796,-0.0015 -2.313,0.4584 -3.1579,1.2815 -0.845,0.8231 -1.3345,1.9441 -1.3639,3.1233 v 0.1148 c -0.0023,2.0185 -0.8053,3.9536 -2.2328,5.3807 -1.4275,1.4271 -3.36289,2.2296 -5.38141,2.2313 z"
      fill="currentColor"
      id="path10"
    />
  </Icon>
);

const SolanaBeachIcon: React.FC<{ size: any }> = ({ size }) => (
  <Icon
    w={size === "xs" ? 3 : size === "sm" ? 4 : 5}
    h={size === "xs" ? 3 : size === "sm" ? 4 : 5}
    viewBox="0 0 44 42"
  >
    <defs>
      <linearGradient
        id="icon-copy-a"
        x1="50%"
        x2="50%"
        y1="-12.918%"
        y2="157.443%"
      >
        <stop offset="0%" stop-color="#F7C300"></stop>
        <stop offset="100%" stop-color="#FA006A"></stop>
      </linearGradient>
      <linearGradient
        id="icon-copy-b"
        x1="50%"
        x2="50%"
        y1="-386.528%"
        y2="591.984%"
      >
        <stop offset="0%" stop-color="#F7C300"></stop>
        <stop offset="100%" stop-color="#FA006A"></stop>
      </linearGradient>
      <linearGradient
        id="icon-copy-c"
        x1="50%"
        x2="50%"
        y1="-386.528%"
        y2="591.984%"
      >
        <stop offset="0%" stop-color="#F7C300"></stop>
        <stop offset="100%" stop-color="#FA006A"></stop>
      </linearGradient>
      <linearGradient
        id="icon-copy-d"
        x1="50%"
        x2="50%"
        y1="-386.528%"
        y2="591.984%"
      >
        <stop offset="0%" stop-color="#F7C300"></stop>
        <stop offset="100%" stop-color="#FA006A"></stop>
      </linearGradient>
    </defs>
    <g fill="none" fillRule="evenodd">
      <path
        fill="url(#icon-copy-a)"
        d="M22.4000003,-5.68434197e-14 C34.1084372,-5.68434197e-14 43.6000006,9.49156345 43.6000006,21.2000003 C43.6000006,21.7345818 43.5043869,22.7036924 43.3131593,24.1073321 C43.2591173,24.5037052 42.9206106,24.7992548 42.5205705,24.7992992 L36.2,24.8000004 L36.2,24.8000004 C35.8686297,24.8000004 35.6000005,25.0686295 35.6000005,25.4000004 C35.6000005,25.7118788 35.8379558,25.9681799 36.1422165,25.9972538 L36.2,26 L42.4163847,25.9992538 C42.6754512,25.9992364 42.8854913,26.2092261 42.8855224,26.4682926 C42.8855263,26.5006762 42.8821768,26.5329731 42.8755274,26.5646666 C42.8755274,26.5646666 42.8755274,26.5646666 42.8755274,26.5646666 C42.797805,26.9351188 42.4710911,27.2004002 42.0925735,27.2004002 L2.70743038,27.2004002 C2.70743038,27.2004002 2.70743038,27.2004002 2.70743038,27.2004002 C2.37574982,27.2004002 2.1068696,26.93152 2.1068696,26.5998394 C2.1068696,26.2681588 2.37574982,25.9992786 2.70743038,25.9992786 C2.70744739,25.9992786 2.7074644,25.9992786 2.70748141,25.9992614 L11.4000002,26 L11.4000002,26 C11.731371,26 12.0000002,25.7313712 12.0000002,25.4000004 C12.0000002,25.0881219 11.7620449,24.8318209 11.4577842,24.802747 L11.4000002,24.8000004 L2.19114952,24.7992703 C1.84143808,24.7992426 1.54376732,24.5446958 1.48945142,24.1992282 C1.44491226,23.915944 1.6384534,23.6501906 1.92173766,23.6056514 C1.94844742,23.601452 1.9754444,23.5993452 2.00248227,23.5993504 L5.40000008,23.6000004 L5.40000008,23.6000004 C5.73137094,23.6000004 6.00000009,23.3313712 6.00000009,23 C6.00000009,22.6881219 5.76204485,22.4318209 5.45778409,22.402747 L5.40000008,22.4000003 L2.0113955,22.3996411 C1.58384682,22.3995598 1.23190785,22.0633297 1.21235342,21.6362284 C1.20411782,21.4563492 1.20000002,21.3109398 1.20000002,21.2000003 C1.20000002,9.49156345 10.6915635,-5.68434197e-14 22.4000003,-5.68434197e-14 Z M40.2000005,22.4000003 L35.8000005,22.4000003 C35.4686297,22.4000003 35.2000005,22.6686295 35.2000005,23.0000003 C35.2000005,23.3118788 35.4379558,23.5681798 35.7422165,23.5972537 L35.8000005,23.6000004 L40.2000005,23.6000004 C40.5313715,23.6000004 40.8000006,23.3313712 40.8000006,23.0000003 C40.8000006,22.6881219 40.5620454,22.4318209 40.2577846,22.402747 L40.2000005,22.4000003 Z"
      ></path>
      <path
        fill="url(#icon-copy-b)"
        d="M27.0000004,35.6000005 C27.3313713,35.6000005 27.6000004,35.8686297 27.6000004,36.2000005 C27.6000004,36.5313714 27.3313713,36.8000005 27.0000004,36.8000005 L11.4000002,36.8000005 C11.0686293,36.8000005 10.8000002,36.5313714 10.8000002,36.2000005 C10.8000002,35.8686297 11.0686293,35.6000005 11.4000002,35.6000005 L27.0000004,35.6000005 Z M39.0000006,33.2000005 C39.3313714,33.2000005 39.6000006,33.4686296 39.6000006,33.8000005 C39.6000006,34.1313714 39.3313714,34.4000005 39.0000006,34.4000005 L31.4000005,34.4000005 C31.0686296,34.4000005 30.8000005,34.1313714 30.8000005,33.8000005 C30.8000005,33.4686296 31.0686296,33.2000005 31.4000005,33.2000005 L39.0000006,33.2000005 Z M25.8000001,33.2000005 C26.1313709,33.2000005 26.4000001,33.4686296 26.4000001,33.8000005 C26.4000001,34.1313714 26.1313709,34.4000005 25.8000001,34.4000005 L5.00000007,34.4000005 C4.66862922,34.4000005 4.40000007,34.1313714 4.40000007,33.8000005 C4.40000007,33.4686296 4.66862922,33.2000005 5.00000007,33.2000005 L25.8000001,33.2000005 Z M8.20000012,30.8000005 C8.53137098,30.8000005 8.80000013,31.0686296 8.80000013,31.4000005 C8.80000013,31.7313713 8.53137098,32.0000005 8.20000012,32.0000005 L0.600000009,32.0000005 C0.268629154,32.0000005 4.05812257e-17,31.7313713 0,31.4000005 C-4.05812257e-17,31.0686296 0.268629154,30.8000005 0.600000009,30.8000005 L8.20000012,30.8000005 Z M28.2000004,30.8000005 C28.5313713,30.8000005 28.8000004,31.0686296 28.8000004,31.4000005 C28.8000004,31.7313713 28.5313713,32.0000005 28.2000004,32.0000005 L15.0000002,32.0000005 C14.6686294,32.0000005 14.4000002,31.7313713 14.4000002,31.4000005 C14.4000002,31.0686296 14.6686294,30.8000005 15.0000002,30.8000005 L28.2000004,30.8000005 Z M43.4000006,30.8000005 C43.7313715,30.8000005 44.0000007,31.0686296 44.0000007,31.4000005 C44.0000007,31.7313713 43.7313715,32.0000005 43.4000006,32.0000005 L35.8000005,32.0000005 C35.4686297,32.0000005 35.2000005,31.7313713 35.2000005,31.4000005 C35.2000005,31.0686296 35.4686297,30.8000005 35.8000005,30.8000005 L43.4000006,30.8000005 Z M12.6000002,30.8000005 C12.931371,30.8000005 13.2000002,31.0686296 13.2000002,31.4000005 C13.2000002,31.7313713 12.931371,32.0000005 12.6000002,32.0000005 L10.6000002,32.0000005 C10.2686293,32.0000005 10.0000001,31.7313713 10.0000001,31.4000005 C10.0000001,31.0686296 10.2686293,30.8000005 10.6000002,30.8000005 L12.6000002,30.8000005 Z M33.0000005,30.8000005 C33.3313713,30.8000005 33.6000005,31.0686296 33.6000005,31.4000005 C33.6000005,31.7313713 33.3313713,32.0000005 33.0000005,32.0000005 L31.0000005,32.0000005 C30.6686296,32.0000005 30.4000005,31.7313713 30.4000005,31.4000005 C30.4000005,31.0686296 30.6686296,30.8000005 31.0000005,30.8000005 L33.0000005,30.8000005 Z M41.4,28.4000004 C41.7313709,28.4000004 42,28.6686296 42,29.0000004 C42,29.3313713 41.7313709,29.6000004 41.4,29.6000004 L2.60000004,29.6000004 C2.26862918,29.6000004 2.00000003,29.3313713 2.00000003,29.0000004 C2.00000003,28.6686296 2.26862918,28.4000004 2.60000004,28.4000004 L41.4,28.4000004 Z"
      ></path>
      <rect
        width="11.6"
        height="1.2"
        x="17.6"
        y="38"
        fill="url(#icon-copy-c)"
        rx=".6"
      ></rect>
      <rect
        width="5.2"
        height="1.2"
        x="20"
        y="40.4"
        fill="url(#icon-copy-d)"
        rx=".6"
      ></rect>
    </g>
  </Icon>
);
